import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from '../common/common.interface';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { persistState } from '@ngneat/elf-persist-state';
import * as localForage from 'localforage';

import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  selectAllEntitiesApply,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { Injectable } from '@angular/core';
import { IDivision } from './division.interface';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'divisions';

export interface IDivisionsProps extends IStoreProps {
  filter: {
    companyId: string;
  };
}

const instance = localForage.createInstance({
  driver: localForage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IDivisionsProps>({
    filter: { companyId: null },
    lastRefreshed: undefined,
  }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IDivision, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class DivisionCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  /**
   * @name divisionList$
   * @description emits stored list of the divisions
   */

  private _selectedCompany$ = new Subject<string>();
  private _filter$ = store.pipe(select((state) => state.filter));

  divisionList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;
  companyDivisions$ = this._selectedCompany$;

  visibleDivisions$ = this._filter$.pipe(
    switchMap((filter) => {
      return store.pipe(
        selectAllEntitiesApply({
          filterEntity(division: IDivision) {
            return (
              division.companyId && division.companyId._id === filter.companyId
            );
          },
        }),
      );
    }),
  );

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    this.refresh();
  }

  public getDivisions(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`divisions/list`, params || {})
      .subscribe((data: IDivisionListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }

  public addDivisions(division: IDivision): Observable<any> {
    this._loading$.next(true);
    return this._api.post('divisions/add', division, {}).pipe(
      tap((division: IDivision) => {
        store.update(addEntities(division));
        this.refresh(true);
        this._loading$.next(false);
      }),
    );
  }

  public editDivisions(data): Observable<any> {
    this._loading$.next(true);
    return this._api.put('divisions/edit', data, {}).pipe(
      tap(() => {
        store.update(updateEntities(data['_id'], data));
        this.refresh(true);
        this._loading$.next(false);
      }),
    );
  }

  public deleteDivision(data): Observable<any> {
    this._loading$.next(true);
    return this._api.delete('divisions', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading$.next(false);
      }),
    );
  }

  public getDivisionInfo(id: string): IDivision {
    const division = store.query(getEntity(id));
    return division;
  }

  public listCompanyDivisionInfo(companyId: string): Observable<IDivision[]> {
    const divisions = store.pipe(
      selectAllEntitiesApply({
        filterEntity: (e) => e.companyId['_id'] === companyId,
      }),
    );
    return divisions;
  }

  public updateFilter(filter: IDivisionsProps['filter']): void {
    store.update(setProp('filter', filter));
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') > 30
    ) {
      this.getDivisions();
    }
  }
}

interface IDivisionListResponse {
  list: IDivision[];
  totalCount: number;
}
